
import { useState } from "react";
import { useForm } from "react-hook-form";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Profile, UserFormValues } from "../types";

async function callAdmin<T = any>(action: string, payload: Record<string, any> = {}): Promise<T> {
  const { data, error } = await supabase.functions.invoke('admin-users', {
    body: { action, ...payload },
  });
  if (error) throw error;
  if (data?.error) throw new Error(data.error);
  return data as T;
}

export const useUserForm = (
  fetchProfiles: () => void,
  fetchCompanies: () => void,
  fetchSupervisors: (role: string, companyName: string) => void
) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [currentUser, setCurrentUser] = useState<Profile | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [errorDialogOpen, setErrorDialogOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState<string | null>(null);
  const [addCompanyDialogOpen, setAddCompanyDialogOpen] = useState(false);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [resetPasswordLoading, setResetPasswordLoading] = useState(false);
  const [successDialogOpen, setSuccessDialogOpen] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");

  const form = useForm<UserFormValues>({
    defaultValues: {
      email: "",
      password: "",
      full_name: "",
      company_name: "",
      role: "",
      supervisor_id: "",
      is_admin: false,
      access_administration: false,
      access_hr: false,
      access_sales: false,
      access_marketing: false,
      access_finance: false,
      access_legal: false,
      access_projects: false,
      access_performance: false,
      is_unit_head: false
    }
  });

  const watchedRole = form.watch("role");
  const watchedCompany = form.watch("company_name");

  const handleAddUser = () => {
    form.reset({
      email: "",
      password: "",
      full_name: "",
      company_name: "",
      role: "",
      supervisor_id: "",
      is_admin: false,
      access_administration: false,
      access_hr: false,
      access_sales: false,
      access_marketing: false,
      access_finance: false,
      access_legal: false,
      access_projects: false,
      access_performance: false,
      is_unit_head: false
    });
    setCurrentUser(null);
    setIsEditing(false);
    setDialogOpen(true);
  };

  const handleEditUser = (profile: Profile) => {
    form.reset({
      email: profile.email,
      password: "",
      full_name: profile.full_name || "",
      company_name: profile.company_name || "",
      role: profile.role || "",
      supervisor_id: profile.supervisor_id || "",
      is_admin: profile.is_admin,
      access_administration: profile.access_administration,
      access_hr: profile.access_hr,
      access_sales: profile.access_sales,
      access_marketing: profile.access_marketing,
      access_finance: profile.access_finance,
      access_legal: profile.access_legal,
      access_projects: profile.access_projects,
      access_performance: profile.access_performance,
      is_unit_head: profile.is_unit_head || false
    });
    setCurrentUser(profile);
    setIsEditing(true);

    if (profile.role && profile.company_name) {
      fetchSupervisors(profile.role, profile.company_name);
    }

    setDialogOpen(true);
  };

  const handleDeleteUser = async (id: string) => {
    setUserToDelete(id);
    setDeleteDialogOpen(true);
  };

  const confirmDeleteUser = async () => {
    if (!userToDelete) return;
    try {
      await callAdmin("delete_user", { id: userToDelete });
      toast.success("User deleted successfully");
      fetchProfiles();
    } catch (error: any) {
      console.error("Error deleting user:", error);
      toast.error("Failed to delete user: " + error.message);
    } finally {
      setDeleteDialogOpen(false);
      setUserToDelete(null);
    }
  };

  const onSubmit = async (values: UserFormValues) => {
    try {
      if (isEditing && currentUser) {
        await callAdmin("update_user", { id: currentUser.id, values });
        toast.success("User updated successfully");
      } else {
        await callAdmin("create_user", { values });
        toast.success("User created successfully");
      }
      setDialogOpen(false);
      fetchProfiles();
      fetchCompanies();
    } catch (error: any) {
      console.error("Error saving user:", error);
      setErrorMessage(`Failed to save user: ${error.message}`);
      setErrorDialogOpen(true);
      toast.error("Failed to save user: " + error.message);
    }
  };

  const handleCompanySelectChange = (value: string) => {
    if (value === "add_new_company") {
      setAddCompanyDialogOpen(true);
    } else {
      form.setValue("company_name", value);
    }
  };

  const handleImportUsers = async () => {
    await fetchProfiles();
    setImportDialogOpen(false);
  };

  const handleResetPassword = async () => {
    if (!currentUser) return;

    setResetPasswordLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('reset-user-password', {
        body: {
          email: currentUser.email,
          user_id: currentUser.id,
          full_name: currentUser.full_name || 'User',
          app_url: typeof window !== 'undefined' ? window.location.origin : undefined,
        }
      });

      if (error) {
        throw error;
      }

      if (data?.success) {
        setSuccessMessage('Password has been successfully reset and email sent to the user.');
        setSuccessDialogOpen(true);
        toast.success('Password reset successfully');
      } else {
        throw new Error(data?.error || 'Failed to reset password');
      }
    } catch (error: any) {
      console.error('Error resetting password:', error);
      const errorMsg = error.message || 'Failed to reset password';
      setErrorMessage(`Failed to reset password: ${errorMsg}`);
      setErrorDialogOpen(true);
      toast.error(errorMsg);
    } finally {
      setResetPasswordLoading(false);
    }
  };

  return {
    form,
    dialogOpen,
    setDialogOpen,
    currentUser,
    isEditing,
    showPassword,
    setShowPassword,
    errorDialogOpen,
    setErrorDialogOpen,
    errorMessage,
    deleteDialogOpen,
    setDeleteDialogOpen,
    userToDelete,
    addCompanyDialogOpen,
    setAddCompanyDialogOpen,
    watchedRole,
    watchedCompany,
    handleAddUser,
    handleEditUser,
    handleDeleteUser,
    confirmDeleteUser,
    onSubmit,
    handleCompanySelectChange,
    importDialogOpen,
    setImportDialogOpen,
    handleImportUsers,
    resetPasswordLoading,
    handleResetPassword,
    successDialogOpen,
    setSuccessDialogOpen,
    successMessage
  };
};
